import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, ApiError } from "@shared/lib/api";
import { Alert, Button, Card, Input, Spinner } from "@shared/components/ui";
import { Logo } from "@shared/components/Logo";
import type { PasswordResetForgotResponse } from "@shared/lib/types";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    setSubmitting(true);

    try {
      await apiFetch<PasswordResetForgotResponse>("/api/v1/auth/password/forgot", {
        method: "POST",
        body: { email },
      });
      setSubmitted(true);
    } catch (err) {
      const errorMessages = err instanceof ApiError ? err.errors : [err instanceof Error ? err.message : "Unknown error"];
      setErrors(errorMessages.length > 0 ? errorMessages : ["Something went wrong"]);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
          <Logo size="lg" className="mb-8" />
          <Card title="Check your email" className="w-full">
            <div className="space-y-4">
              <p className="text-muted">
                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              <p className="text-sm text-muted">
                The link will expire in 1 hour. If you don't see the email, check your spam folder.
              </p>
              <div className="pt-4">
                <Link
                  to="/login"
                  className="font-medium text-primary hover:underline"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
        <Logo size="lg" className="mb-8" />
        <Card title="Forgot your password?" className="w-full">
          <p className="mb-4 text-muted">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          <form className="space-y-4" onSubmit={onSubmit}>
            <Alert items={errors} />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
            <div className="flex items-center justify-between gap-3">
              <Button type="submit" disabled={submitting || !email.trim()}>
                {submitting ? "Sending..." : "Send reset link"}
              </Button>
              {submitting ? <Spinner /> : null}
            </div>
          </form>

          <div className="mt-4 text-sm text-muted">
            Remember your password?{" "}
            <Link className="font-medium text-foreground underline" to="/login">
              Sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
